import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Checkbox, Chip, Tab, Tabs, Typography } from '@material-ui/core'
import {
	Product,
	Section,
} from '../../MarketingCampaigns.promo.services'
import { displayPriceFormat } from '../../../../utils/tools'
import { pluralize } from '../../../../utils/stringUtils'
import {
	useCampaignModalSkeletonStyles,
	G,
	CAMPAIGN_ICON_TEXT_GAP_PX,
} from '../CampaignModalSkeleton.styles'
import {
	instructionBandStyle,
	optionCaretStyle,
	optionChipStyle,
	optionCountTextStyle,
	optionExpandedPanelStyle,
	optionExpandToggleStyle,
	optionLabelContainerStyle,
	optionNameStyle,
	optionRadioDotStyle,
	optionRadioStyle,
	optionRowStyle,
	optionsEmptyContainerStyle,
	optionsEmptyTextStyle,
	selectedChipStyle,
	selectedSummaryContainerStyle,
	selectedSummaryTextStyle,
	tabCountChipStyle,
	tabLabelStyle,
	tabsRootStyle,
} from './ProductScopeSelector.styles'

export interface CampaignOptionList {
	id: string
	name: string
	count: number
	type: string
	isPrivate: boolean
	options: Array<{ key: string; name: string; price?: number }>
}

type ScopeTab = 'products' | 'options'

export interface ProductScopeSelectorProps {
	sections: Section[]
	selectedKeys: string[]
	onToggle: (product: Product, sectionId: string) => void
	disabledKeys?: string[]
	showRankBadge?: boolean
	hideSummary?: boolean
	/** Overrides default scroll area height (px) for the products/options lists */
	maxListHeight?: number
	instructions?: string
	embedded?: boolean
	withOptionsTab?: boolean
	optionLists?: CampaignOptionList[]
	tabLabelProducts?: string
	activeScopeTab?: ScopeTab
	selectedOptionListKey?: string | null
	onOptionListSelect?: (key: string) => void
	onScopeTabChange?: (tab: 'products' | 'options') => void
	emptyOptionListsMessage?: string
	optionListsLoading?: boolean
	optionListsError?: boolean
}

const DEFAULT_INSTRUCTIONS = 'Sélectionnez les produits éligibles à la promotion'

function findProductInSections(
	sections: Section[],
	productKey: string,
): { product: Product; sectionId: string } | null {
	for (const section of sections) {
		const product = section.products.find((p) => p.key === productKey)
		if (product) {
			return { product, sectionId: section.id }
		}
	}
	return null
}

export function ProductScopeSelector({
	sections,
	selectedKeys,
	onToggle,
	disabledKeys = [],
	showRankBadge = false,
	hideSummary = false,
	maxListHeight,
	instructions = DEFAULT_INSTRUCTIONS,
	embedded = false,
	withOptionsTab = false,
	optionLists = [],
	tabLabelProducts = 'Produits',
	activeScopeTab,
	selectedOptionListKey = null,
	onOptionListSelect,
	onScopeTabChange,
	emptyOptionListsMessage,
	optionListsLoading = false,
	optionListsError = false,
}: ProductScopeSelectorProps) {
	const classes = useCampaignModalSkeletonStyles()
	const disabledSet = useMemo(() => new Set(disabledKeys), [disabledKeys])

	const [internalScopeTab, setInternalScopeTab] = useState<ScopeTab>(
		activeScopeTab ?? 'products',
	)
	const scopeTab = activeScopeTab ?? internalScopeTab

	useEffect(() => {
		if (activeScopeTab) {
			setInternalScopeTab(activeScopeTab)
		}
	}, [activeScopeTab])

	const handleScopeTab = useCallback(
		(_: React.ChangeEvent<Record<string, unknown>>, v: ScopeTab) => {
			setInternalScopeTab(v)
			onScopeTabChange?.(v)
		},
		[onScopeTabChange],
	)

	const [expandedOptionList, setExpandedOptionList] = useState<string | null>(null)

	const productsPanel = (
		<Box>
			<Box style={instructionBandStyle}>
				{instructions}
			</Box>
			<Box
				className={classes.scrollList}
				style={maxListHeight != null ? { maxHeight: maxListHeight } : undefined}
			>
				{sections.map((section) => (
					<Box key={section.id}>
						<Box className={classes.stickyCat}>{section.name}</Box>
						{section.products.map((product) => {
							const sel = selectedKeys.includes(product.key)
							const rank = selectedKeys.indexOf(product.key) + 1
							const rowDisabled = disabledSet.has(product.key)
							return (
								<Box
									key={product.key}
									className={classes.rowGreen}
									onClick={() => !rowDisabled && onToggle(product, section.id)}
									style={{
										display: 'flex',
										alignItems: 'center',
										padding: '9px 14px',
										gap: CAMPAIGN_ICON_TEXT_GAP_PX,
										cursor: rowDisabled ? 'not-allowed' : 'pointer',
										background: sel
											? 'rgba(0, 153, 115, 0.06)'
											: '#fff',
										borderLeft: sel ? `3px solid ${G}` : '3px solid transparent',
										transition: 'background .1s',
										opacity: rowDisabled ? 0.45 : 1,
									}}
								>
									<Checkbox
										checked={sel}
										color="primary"
										disabled={rowDisabled}
										onClick={(e) => e.stopPropagation()}
										onChange={() => !rowDisabled && onToggle(product, section.id)}
										style={{ padding: 0 }}
									/>
									<Typography
										variant="body2"
										style={{
											flex: 1,
											color: sel ? G : '#212121',
											fontWeight: sel ? 500 : 400,
										}}
									>
										{product.name}
									</Typography>
									{sel && showRankBadge && (
										<Box
											style={{
												fontSize: 11,
												fontWeight: 700,
												color: '#fff',
												background: G,
												borderRadius: '50%',
												width: 18,
												height: 18,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												flexShrink: 0,
											}}
										>
											{rank}
										</Box>
									)}
									<Typography variant="caption" style={{ color: '#9e9e9e' }}>
										{displayPriceFormat(product.price)}
									</Typography>
								</Box>
							)
						})}
					</Box>
				))}
			</Box>
			{!hideSummary && selectedKeys.length > 0 && (
				<Box style={selectedSummaryContainerStyle}>
					<Typography variant="caption" style={selectedSummaryTextStyle}>
						{selectedKeys.length} produit{pluralize(selectedKeys.length)}{' '}
						sélectionné
						{pluralize(selectedKeys.length)}
						{showRankBadge ? ', dans l\u2019ordre :' : ' :'}
					</Typography>
					<Box
						display="flex"
						flexWrap="wrap"
						style={{ gap: CAMPAIGN_ICON_TEXT_GAP_PX }}
					>
						{selectedKeys.map((id, i) => {
							const found = findProductInSections(sections, id)
							if (!found) {
								return null
							}
							const { product: p } = found
							const rankPrefix = showRankBadge ? `${i + 1}. ` : ''
							return (
								<Chip
									key={id}
									label={`${rankPrefix}${p.name} · ${displayPriceFormat(p.price)}`}
									size="small"
									variant="outlined"
									style={selectedChipStyle(G)}
								/>
							)
						})}
					</Box>
				</Box>
			)}
		</Box>
	)

	const optionsPanel = (
		<Box>
			<Box style={instructionBandStyle}>
				Sélectionnez une liste d&apos;options éligible
			</Box>
			<Box
				className={classes.scrollList}
				style={maxListHeight != null ? { maxHeight: maxListHeight } : undefined}
			>
				{optionListsLoading || optionListsError || optionLists.length === 0 ? (
					<Box style={optionsEmptyContainerStyle}>
						<Typography variant="caption" style={optionsEmptyTextStyle}>
							{emptyOptionListsMessage ||
								"Aucune liste d'options en commun."}
						</Typography>
					</Box>
				) : (
					optionLists.filter((ol) => !ol.isPrivate).map((ol) => {
						const sel = selectedOptionListKey === ol.id
						const exp = expandedOptionList === ol.id
						return (
							<Box key={ol.id}>
								<Box
									onClick={() => onOptionListSelect?.(ol.id)}
									style={optionRowStyle(sel, G)}
									className={classes.rowGreen}
								>
									<Box style={optionRadioStyle(sel, G)}>
										{sel && <Box style={optionRadioDotStyle(G)} />}
									</Box>
									<Box style={optionLabelContainerStyle}>
										<Typography
											variant="body2"
											style={optionNameStyle(sel, G)}
										>
											{ol.name}
										</Typography>
										<Typography variant="caption" style={optionCountTextStyle}>
											{ol.count} option{pluralize(ol.count)}
										</Typography>
									</Box>
									<Box
										onClick={(e) => {
											e.stopPropagation()
											setExpandedOptionList(exp ? null : ol.id)
										}}
										style={optionExpandToggleStyle}
									>
										<Typography style={optionCaretStyle(exp)}>
											▾
										</Typography>
									</Box>
								</Box>
								{exp && (
									<Box style={optionExpandedPanelStyle(sel, G)}>
										{ol.options.map((opt) => (
											<Chip
												key={opt.key}
												label={opt.price != null ? `${opt.name} · ${displayPriceFormat(opt.price)}` : opt.name}
												size="small"
												variant="outlined"
												style={optionChipStyle}
											/>
										))}
									</Box>
								)}
							</Box>
						)
					})
				)}
			</Box>
		</Box>
	)

	if (embedded) {
		return <Box>{productsPanel}</Box>
	}

	return (
		<Box className={classes.scopeTabPanel}>
			<Tabs
				value={scopeTab}
				onChange={handleScopeTab}
				indicatorColor="primary"
				textColor="primary"
				variant="fullWidth"
				style={tabsRootStyle}
			>
				<Tab
					value="products"
					label={
						<Box
							display="flex"
							alignItems="center"
							style={tabLabelStyle(CAMPAIGN_ICON_TEXT_GAP_PX)}
						>
							<span>{tabLabelProducts}</span>
							{scopeTab === 'products' && (
								<Chip
									label={selectedKeys.length}
									size="small"
									style={tabCountChipStyle(G)}
								/>
							)}
						</Box>
					}
				/>
				{withOptionsTab && (
					<Tab
						value="options"
						label={
							<Box
								display="flex"
								alignItems="center"
								style={tabLabelStyle(CAMPAIGN_ICON_TEXT_GAP_PX)}
							>
								<span>Listes d&apos;options</span>
								{scopeTab === 'options' && selectedOptionListKey && (
									<Chip
										label={1}
										size="small"
										style={tabCountChipStyle(G)}
									/>
								)}
							</Box>
						}
					/>
				)}
			</Tabs>
			{scopeTab === 'products' && productsPanel}
			{withOptionsTab && scopeTab === 'options' && optionsPanel}
		</Box>
	)
}
